import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const DEFAULT_REDIRECTION_SECONDS = 60;

function resolveRedirectionSeconds(seconds?: number | null): number {
    if (typeof seconds !== 'number' || !Number.isFinite(seconds)) {
        return DEFAULT_REDIRECTION_SECONDS;
    }
    return seconds;
}

export function resolveCallbackRedirect(signData?: any) {
    return {
        callbackUrl: signData?.signer_data?.callback_url as string | undefined,
        redirectionSeconds: resolveRedirectionSeconds(signData?.signer_data?.callback_redirection_seconds),
    };
}

export function shouldRedirectToCallbackImmediately(signData?: any): boolean {
    const { callbackUrl, redirectionSeconds } = resolveCallbackRedirect(signData);
    return Boolean(callbackUrl) && redirectionSeconds <= 0;
}

export function redirectToCallbackNow(callbackUrl?: string | null) {
    if (!callbackUrl) return;
    window.location.replace(callbackUrl);
}

export function useCallbackRedirect(callbackUrl?: string | null, redirectionSeconds?: number | null) {
    const initialCountdown = resolveRedirectionSeconds(redirectionSeconds);
    const isImmediate = Boolean(callbackUrl) && initialCountdown <= 0;
    const [countdown, setCountdown] = useState(initialCountdown);
    const redirectedRef = useRef(false);

    const redirectOnce = (url: string) => {
        if (redirectedRef.current) return;
        redirectedRef.current = true;
        redirectToCallbackNow(url);
    };

    useLayoutEffect(() => {
        if (!isImmediate || !callbackUrl) return;
        redirectOnce(callbackUrl);
    }, [isImmediate, callbackUrl]);

    useEffect(() => {
        setCountdown(initialCountdown);
    }, [initialCountdown]);

    useEffect(() => {
        if (!callbackUrl || isImmediate) return;

        if (countdown <= 0) {
            redirectOnce(callbackUrl);
            return;
        }

        const timer = setTimeout(() => setCountdown((prev) => prev - 1), 1000);
        return () => clearTimeout(timer);
    }, [callbackUrl, countdown, isImmediate]);

    const redirectToCallback = () => {
        if (callbackUrl) {
            window.location.href = callbackUrl;
        }
    };

    const showCountdown = Boolean(callbackUrl) && initialCountdown > 0;

    return { countdown: showCountdown ? countdown : null, redirectToCallback };
}
